import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import { useMutation } from "@apollo/client";

import SeoMeta from "../../../../platform/components/common/seoMeta";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations/business";
import {
  APPLICANT_SUBSCRIPTION_PLANS,
  ApplicantSubscriptionPlan,
  ApplicantSubscriptionPlanCode,
  ApplicantSubscriptionRecord,
  FREE_APPLICANT_PLAN,
  cancelApplicantSubscription,
  createApplicantSubscription,
  getApplicantSubscriptionStatusLabel,
  getLatestApplicantSubscription,
  listApplicantSubscriptionPlans,
} from "../api/applicantSubscriptions";

const SUPPORT_EMAIL = "support@plany365.com";
const NO_REPLY_EMAIL = "no-reply@plany365.com";
const SHARE_PATH = "/care/jobs/pricing";

type IdentityForm = {
  fullName: string;
  email: string;
  phone: string;
  notes: string;
};

function buildSupportEmailText(
  subscription: ApplicantSubscriptionRecord,
  form: IdentityForm
) {
  return [
    "New care jobs applicant subscription request",
    "",
    `Record id: ${subscription.id}`,
    `Plan: ${subscription.planName} ($${subscription.priceMonthlyUsd}/month)`,
    `Status: ${subscription.status}`,
    `Full name: ${subscription.fullName}`,
    `Email: ${subscription.email}`,
    `Phone: ${subscription.phone || "-"}`,
    `User id: ${subscription.userId ?? "-"}`,
    `Stripe provider: ${subscription.billingProvider}`,
    `Stripe customer id: ${subscription.stripeCustomerId || "-"}`,
    `Stripe subscription id: ${subscription.stripeSubscriptionId || "-"}`,
    "",
    "Notes",
    form.notes.trim() || "-",
  ].join("\n");
}

function buildCustomerConfirmationText(subscription: ApplicantSubscriptionRecord) {
  return [
    `Hi ${subscription.fullName},`,
    "",
    `We received your ${subscription.planName} care jobs membership request.`,
    "We will follow up with the next step to activate your membership account.",
    "",
    "Until then, you can still use the free caregiver experience:",
    "- Search jobs",
    "- View job details",
    "- Apply to open jobs",
    "- Use your caregiver profile and alerts",
    "",
    "Jobs: https://plany365.com/care/jobs",
    "Profile: https://plany365.com/care/myjobs",
    "",
    "Thank you,",
    "Plany365 Care Jobs",
  ].join("\n");
}

function PlanCard({
  plan,
  currentPlanCode,
  currentStatus,
  loadingPlanCode,
  onSelect,
}: {
  plan: ApplicantSubscriptionPlan;
  currentPlanCode?: string | null;
  currentStatus?: string | null;
  loadingPlanCode?: string | null;
  onSelect: (plan: ApplicantSubscriptionPlan) => void;
}) {
  const isCurrent = currentPlanCode === plan.code && currentStatus !== "CANCELED";
  const actionLabel = isCurrent
    ? `${plan.name} ${getApplicantSubscriptionStatusLabel(currentStatus)}`
    : `Start ${plan.name}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.25, sm: 2.75 },
        borderRadius: 3,
        borderColor: isCurrent ? "#7ea6ff" : "#dbe3ef",
        boxShadow: isCurrent ? "0 18px 40px rgba(37, 99, 235, 0.12)" : "none",
        background: isCurrent
          ? "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, #ffffff 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#14324a" }}>
              {plan.name}
            </Typography>
            {plan.recommended ? (
              <Chip size="small" color="primary" label="Recommended" />
            ) : null}
            {isCurrent ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={getApplicantSubscriptionStatusLabel(currentStatus)}
              />
            ) : null}
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#173550" }}>
            ${plan.monthlyPriceUsd}
            <Typography
              component="span"
              variant="body1"
              sx={{ ml: 0.5, color: "text.secondary", fontWeight: 500 }}
            >
              / month
            </Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: "#48657a" }}>
            {plan.description}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          {plan.highlights.map((item) => (
            <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
              <CheckCircleOutlineRoundedIcon sx={{ mt: "2px", color: "#0f7ca8", fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: "#31556f" }}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          variant={isCurrent ? "outlined" : "contained"}
          onClick={() => onSelect(plan)}
          disabled={loadingPlanCode === plan.code}
          sx={{ borderRadius: 999, alignSelf: "flex-start", px: 2.25 }}
        >
          {loadingPlanCode === plan.code ? "Saving..." : actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function CareJobPricingPage(): JSX.Element {
  const { providerUser, user } = React.useContext(UserContext) as any;
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [form, setForm] = React.useState<IdentityForm>({
    fullName: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [currentSubscription, setCurrentSubscription] =
    React.useState<ApplicantSubscriptionRecord | null>(null);
  const [plans, setPlans] =
    React.useState<ApplicantSubscriptionPlan[]>(APPLICANT_SUBSCRIPTION_PLANS);
  const [loadingCurrent, setLoadingCurrent] = React.useState(true);
  const [submittingPlanCode, setSubmittingPlanCode] = React.useState<string | null>(null);
  const [canceling, setCanceling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const userId = Number(providerUser?.userId || user?.id || 0) || null;
  const effectiveEmail = String(form.email || providerUser?.email || user?.email || "").trim();

  React.useEffect(() => {
    const displayName = String(
      providerUser?.username || user?.username || user?.name || ""
    ).trim();
    const email = String(providerUser?.email || user?.email || "").trim();
    const phone = String(providerUser?.phone || user?.phone || "").trim();

    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || displayName,
      email: prev.email || email,
      phone: prev.phone || phone,
    }));
  }, [providerUser, user]);

  const refreshCurrentSubscription = React.useCallback(async () => {
    if (!userId && !effectiveEmail) {
      setCurrentSubscription(null);
      setLoadingCurrent(false);
      return;
    }

    setLoadingCurrent(true);
    try {
      const latest = await getLatestApplicantSubscription({
        userId,
        email: effectiveEmail,
      });
      setCurrentSubscription(latest);
    } catch {
      setCurrentSubscription(null);
    } finally {
      setLoadingCurrent(false);
    }
  }, [effectiveEmail, userId]);

  React.useEffect(() => {
    refreshCurrentSubscription();
  }, [refreshCurrentSubscription]);

  React.useEffect(() => {
    let active = true;

    listApplicantSubscriptionPlans()
      .then((items) => {
        if (active && items.length) {
          setPlans(items);
        }
      })
      .catch(() => {
        if (active) {
          setPlans(APPLICANT_SUBSCRIPTION_PLANS);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleFieldChange = React.useCallback(
    (field: keyof IdentityForm, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSelectPlan = React.useCallback(
    async (plan: ApplicantSubscriptionPlan) => {
      setError(null);
      setSuccess(null);

      if (!form.fullName.trim() || !form.email.trim()) {
        setError("Please add your full name and email before choosing a membership.");
        return;
      }

      setSubmittingPlanCode(plan.code);
      try {
        const response = await createApplicantSubscription({
          planCode: plan.code as ApplicantSubscriptionPlanCode,
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          userId,
          notes: form.notes.trim(),
        });

        const subscription = response.subscription;
        setCurrentSubscription(subscription);

        try {
          await sendEmail({
            variables: {
              from_email: NO_REPLY_EMAIL,
              to: SUPPORT_EMAIL,
              subject: `[Care Jobs Membership] ${subscription.planName} - ${subscription.fullName}`,
              text: buildSupportEmailText(subscription, form),
            },
          });

          await sendEmail({
            variables: {
              from_email: NO_REPLY_EMAIL,
              to: subscription.email,
              subject: `We received your ${subscription.planName} membership request`,
              text: buildCustomerConfirmationText(subscription),
            },
          });
        } catch (mailError) {
          console.error("Failed to send membership email", mailError);
        }

        setSuccess(
          `${subscription.planName} membership request received. We will contact you with the next step.`
        );
      } catch (submitError: any) {
        setError(
          String(
            submitError?.message ||
              "We could not save your membership request right now. Please try again."
          )
        );
      } finally {
        setSubmittingPlanCode(null);
      }
    },
    [form, sendEmail, userId]
  );

  const handleCancelCurrent = React.useCallback(async () => {
    if (!currentSubscription?.id) return;

    setError(null);
    setSuccess(null);
    setCanceling(true);
    try {
      const response = await cancelApplicantSubscription(currentSubscription.id);
      setCurrentSubscription(response.subscription);
      setSuccess("Membership request canceled.");
    } catch (cancelError: any) {
      setError(
        String(
          cancelError?.message ||
            "We could not cancel the membership request right now. Please try again."
        )
      );
    } finally {
      setCanceling(false);
    }
  }, [currentSubscription]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 5 },
        background: "linear-gradient(180deg, #f4fbff 0%, #ffffff 40%, #f8f6ef 100%)",
      }}
    >
      <SeoMeta
        title="Care Job Membership Pricing | Plany365"
        description="Compare free, standard, and premium caregiver job membership options on Plany365."
        url={SHARE_PATH}
      />

      <Paper
        elevation={0}
        sx={{
          maxWidth: 1120,
          mx: "auto",
          p: { xs: 2.5, sm: 4 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(255,255,255,0.97)",
          boxShadow: "0 24px 60px rgba(18, 44, 67, 0.08)",
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1.25}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: "0.12em", color: "text.secondary", fontWeight: 700 }}
            >
              Care Jobs Membership
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.05,
                color: "#14324a",
                fontSize: { xs: "2rem", sm: "2.75rem" },
              }}
            >
              Choose your caregiver plan
            </Typography>
            <Typography variant="body1" sx={{ color: "#49657a", fontSize: "1.05rem" }}>
              Free search and apply stay available. Standard and Premium add stronger
              follow-up, faster alerts, and better visibility when matching roles open.
            </Typography>
          </Stack>

          {loadingCurrent ? (
            <Alert severity="info">Checking your current membership request...</Alert>
          ) : currentSubscription ? (
            <Alert
              severity={currentSubscription.status === "CANCELED" ? "warning" : "success"}
              action={
                currentSubscription.status !== "CANCELED" ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleCancelCurrent}
                    disabled={canceling}
                  >
                    {canceling ? "Canceling..." : "Cancel Request"}
                  </Button>
                ) : undefined
              }
            >
              Current membership:{" "}
              <strong>
                {currentSubscription.planName} {getApplicantSubscriptionStatusLabel(currentSubscription.status)}
              </strong>
              {currentSubscription.status !== "CANCELED"
                ? ". You can switch plans below any time."
                : ". You can start a new plan below any time."}
            </Alert>
          ) : (
            <Alert severity="info">
              You are currently on the free caregiver experience. Search and apply stay free.
            </Alert>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              borderColor: "#dbe3ef",
              background: "#fbfdff",
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#173550" }}>
                Your membership details
              </Typography>
              <Typography variant="body2" sx={{ color: "#48657a" }}>
                We use this to create your membership account and contact you about your
                plan.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Full name"
                  value={form.fullName}
                  onChange={(event) => handleFieldChange("fullName", event.target.value)}
                />
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={(event) => handleFieldChange("email", event.target.value)}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={form.phone}
                  onChange={(event) => handleFieldChange("phone", event.target.value)}
                />
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  placeholder="Shift preference, live-in, certifications, urgent search..."
                  value={form.notes}
                  onChange={(event) => handleFieldChange("notes", event.target.value)}
                />
              </Stack>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.25, sm: 2.75 },
                borderRadius: 3,
                borderColor: "#dbe3ef",
                background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.75}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#14324a" }}>
                    {FREE_APPLICANT_PLAN.name}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#173550" }}>
                    $0
                    <Typography
                      component="span"
                      variant="body1"
                      sx={{ ml: 0.5, color: "text.secondary", fontWeight: 500 }}
                    >
                      / month
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#48657a" }}>
                    {FREE_APPLICANT_PLAN.description}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  {FREE_APPLICANT_PLAN.highlights.map((item) => (
                    <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircleOutlineRoundedIcon
                        sx={{ mt: "2px", color: "#0f7ca8", fontSize: 18 }}
                      />
                      <Typography variant="body2" sx={{ color: "#31556f" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  component={RouterLink}
                  to="/care/jobs"
                  variant="outlined"
                  sx={{ borderRadius: 999, alignSelf: "flex-start", px: 2.25 }}
                >
                  Continue Free
                </Button>
              </Stack>
            </Paper>

            {plans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                currentPlanCode={currentSubscription?.planCode}
                currentStatus={currentSubscription?.status}
                loadingPlanCode={submittingPlanCode}
                onSelect={handleSelectPlan}
              />
            ))}
          </Box>

          <Divider />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Typography variant="body2" sx={{ color: "#48657a" }}>
              You can still search, apply, and manage your caregiver profile right now.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Button component={RouterLink} to="/care/jobs" variant="outlined">
                Browse Jobs
              </Button>
              <Button component={RouterLink} to="/care/myjobs" variant="contained">
                My Jobs & Profile
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
